import React from 'react'
import { Formik, Form, Field } from 'formik'
import styled from 'styled-components'
import { IProductListItemNode } from '../../../../../../modules/API/Defaults/models/productListNode'
import { IGrowerItem } from '../../../../../../modules/API/Defaults/models/growers'
import { IWarhouseListItem } from '../../../../../../modules/API/Defaults/models/warhouseList'
import { EXPIRATION_FILTER, IExpirationReportForm } from '../types/form'
import { FormGrid, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout'
import { AccentButton } from '../../../../../../generic/ui/Button'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import ProductForSortingSelector from '../../../../../../generic/ui/Selectors/ProductForSortingSelector'
import GrowerSelector from '../../../../../../generic/ui/Selectors/GrowerSelector'
import WarehouseSelector from '../../../../../../generic/ui/Selectors/WarehouseSelector'
import OptionSelect, { IOption } from '../../../../../../generic/ui/Selectors/OptionSelect'
import OccupancySelector, { OCCUPANCY, OccupancyValue } from '../../../../../../generic/ui/Selectors/OccupancySelector'
import MapPinIcon from '../../../../../../generic/icons/IconLib/MapPinIcon'
import CalendarIcon from '../../../../../../generic/icons/IconLib/CalendarIcon'
import FilterIcon from '../../../../../../generic/icons/IconLib/FilterIcon'

const FILTER_OPTIONS: IOption[] = [
    { value: EXPIRATION_FILTER.ALL, label: 'הכל' },
    { value: EXPIRATION_FILTER.EXPIRED, label: 'פג תוקף בלבד' },
    { value: EXPIRATION_FILTER.DAYS_TO_EXPIRATION, label: 'ימים לפג תוקף' },
]

type Props = {
    handleSubmit: (data: IExpirationReportForm) => void
    products: IProductListItemNode[]
    productId: number
    setProductId: React.Dispatch<React.SetStateAction<number>>
    growerList: IGrowerItem[]
    growerId: number
    setGrowerId: React.Dispatch<React.SetStateAction<number>>
    warehouseId: number
    warehouseList: IWarhouseListItem[]
    formValues?: IExpirationReportForm | null
    closeFunc?: (flag?: boolean) => void
}
const ExpirationReportForm = ({
    handleSubmit,
    products,
    productId,
    setProductId,
    growerList,
    growerId,
    setGrowerId,
    warehouseId,
    warehouseList,
    formValues
}: Props) => {
  const validateExpirationReportForm = (values: any) => {
    const errors: any = {}

    return errors
  }
  return (
    <Formik
      initialValues={{
        productId: formValues?.productId ?? (productId || 0),
        growerId: formValues?.growerId ?? (growerId || 0),
        location: formValues?.location ?? '',
        daysToExpiration: formValues?.daysToExpiration ?? '',
        warehouseId: formValues?.warehouseId ?? (warehouseId || 0),
        expirationFilter: formValues?.expirationFilter ?? EXPIRATION_FILTER.ALL,
        occupancy: formValues?.occupancy ?? OCCUPANCY.ALL,
      }}
      validate={validateExpirationReportForm}
      enableReinitialize
      onSubmit={(values) => handleSubmit(values)}
    >
      <StyledForm
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.requestSubmit()
          }
        }}
      >
        <FormGrid>
            <Field name="occupancy">
                {({
                    field,
                    form,
                }: {
                    field: { value: OccupancyValue }
                    form: { setFieldValue: (name: string, value: number) => void }
                }) => (
                    <FormField label="תפוסה" fullWidth>
                        <OccupancySelector
                            value={field.value}
                            onSelect={(value) => form.setFieldValue('occupancy', value)}
                        />
                    </FormField>
                )}
            </Field>
            <Field name="productId">
                {({
                    field,
                    form,
                }: {
                    field: { value: number }
                    form: { setFieldValue: (name: string, value: number) => void }
                }) => (
                    <FormField label="בחר פריט" fullWidth>
                        <ProductForSortingSelector
                            items={products}
                            value={products.find((p) => p.ProductID === field.value) ?? null}
                            onSelect={(p) => form.setFieldValue('productId', p?.ProductID ?? 0)}
                            allowClear
                        />
                    </FormField>
                )}
            </Field>
            <Field name="growerId">
                {({
                    field,
                    form,
                }: {
                    field: { value: number }
                    form: { setFieldValue: (name: string, value: number) => void }
                }) => (
                    <FormField label="בחר ספק">
                        <GrowerSelector
                            growers={growerList}
                            value={growerList.find((g) => g.GrowerID === field.value) ?? null}
                            onSelect={(g) => form.setFieldValue('growerId', g?.GrowerID ?? 0)}
                            allowClear
                        />
                    </FormField>
                )}
            </Field>
            <Field name="warehouseId">
                {({
                    field,
                    form,
                }: {
                    field: { value: number }
                    form: { setFieldValue: (name: string, value: number) => void }
                }) => (
                    <FormField label="בחר מחסן">
                        <WarehouseSelector
                            warehouses={warehouseList}
                            value={warehouseList.find((w) => w.WarehouseID === field.value) ?? null}
                            onSelect={(w) => form.setFieldValue('warehouseId', w?.WarehouseID ?? 0)}
                            allowClear
                        />
                    </FormField>
                )}
            </Field>
            <Field name="location">
                {({
                    field,
                    form,
                }: {
                    field: { value: string }
                    form: { setFieldValue: (name: string, value: string) => void }
                }) => (
                    <FormField label="איתור" fullWidth>
                        <IconTextField
                            value={field.value || ''}
                            onChange={(e) => form.setFieldValue('location', e.target.value)}
                            leadingIcon={<MapPinIcon />}
                            placeholder="איתור"
                        />
                    </FormField>
                )}
            </Field>
            <Field name="expirationFilter">
                {({
                    field,
                    form,
                }: {
                    field: { value: number }
                    form: { setFieldValue: (name: string, value: number | string) => void }
                }) => (
                    <>
                        <FormField label="נתונים להצגה" fullWidth={field.value !== EXPIRATION_FILTER.DAYS_TO_EXPIRATION}>
                            <OptionSelect
                                options={FILTER_OPTIONS}
                                value={FILTER_OPTIONS.find((o) => o.value === field.value) ?? null}
                                onSelect={(o) => {
                                    const next = o?.value ?? EXPIRATION_FILTER.ALL
                                    form.setFieldValue('expirationFilter', next)
                                    if (next !== EXPIRATION_FILTER.DAYS_TO_EXPIRATION) {
                                        form.setFieldValue('daysToExpiration', '')
                                    }
                                }}
                                leadingIcon={<FilterIcon />}
                            />
                        </FormField>
                        {field.value === EXPIRATION_FILTER.DAYS_TO_EXPIRATION && (
                            <Field name="daysToExpiration">
                                {({
                                    field: daysField,
                                    form: daysForm,
                                }: {
                                    field: { value: string }
                                    form: { setFieldValue: (name: string, value: string) => void }
                                }) => (
                                    <FormField label="ימים לפג תוקף">
                                        <IconTextField
                                            type="number"
                                            value={daysField.value || ''}
                                            onChange={(e) => daysForm.setFieldValue('daysToExpiration', e.target.value)}
                                            leadingIcon={<CalendarIcon />}
                                            placeholder="ימים לפג תוקף"
                                        />
                                    </FormField>
                                )}
                            </Field>
                        )}
                    </>
                )}
            </Field>
        </FormGrid>
        <FormFooter>
            <AccentButton type="submit" fullWidth={false}>
                חפש
            </AccentButton>
        </FormFooter>
      </StyledForm>
    </Formik>
  )
}

const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

export default ExpirationReportForm
